import {FC, useCallback, useEffect, useRef, useState} from 'react';
import {Box, Button, CircularProgress, Drawer, IconButton, Typography} from '@mui/material';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {useIntl} from 'react-intl';
import {aiAgentApi} from 'src/api';
import {useAppSelector, useBusinessId} from 'src/app/hooks';
import {AiAgentChartData, AiAgentMessage, AiAgentPdfExport, AiAgentSession, AiAgentTableData, AiAgentUsage, AuthUserDTO} from 'src/types';
import {getAuthToken} from './utils/getAuthToken';
import {useStreamChat, StreamDoneResult} from './hooks/useStreamChat';
import {ChatMessage} from './components/ChatMessage';
import {ChatInput} from './components/ChatInput';
import {SessionSidebar} from './components/SessionSidebar';
import {ToolCallIndicator} from './components/ToolCallIndicator';
import {ApprovalCard} from './components/ApprovalCard';
import {UsageBar} from './components/UsageBar';
import {ContactSupportDialog} from './components/ContactSupportDialog';
import {aiAgentSx} from './AiAgent.sx';

const getSessionStorageKey = (userId: number | undefined) =>
    userId ? `ai_agent_session_id:${userId}` : null;

interface ChatMsg extends AiAgentMessage {
    charts?: AiAgentChartData[];
    tables?: AiAgentTableData[];
    exports?: AiAgentPdfExport[];
    truncated?: boolean;
    isError?: boolean;
}

const visualCount = (m: ChatMsg): number =>
    (m.charts?.length ?? 0) + (m.tables?.length ?? 0) + (m.exports?.length ?? 0);

// Reloaded history stores each agent turn as its own message. When a tool-only turn
// (e.g. an exportPdf call with no narration) carries visuals but empty text, and the
// closing narration lands in the NEXT turn's message, they must render as ONE bubble
// — exactly as the live stream shows them (the empty placeholder absorbs the next
// turn's text). Without this, the "Download PDF" button (or a chart/table) reappears
// in its own separate bubble after a reload. Merge a visual-only assistant message
// forward into the following assistant message; chains through several such turns.
const mergeVisualOnlyBubbles = (msgs: ChatMsg[]): ChatMsg[] => {
    const out: ChatMsg[] = [];
    for (const m of msgs) {
        const prev = out[out.length - 1];
        const prevVisualOnly = prev && prev.role === 'assistant' && !prev.content?.trim() && visualCount(prev) > 0;
        if (prevVisualOnly && m.role === 'assistant') {
            out[out.length - 1] = {
                ...prev,
                content: prev.content?.trim() ? prev.content : m.content,
                charts: [...(prev.charts ?? []), ...(m.charts ?? [])],
                tables: [...(prev.tables ?? []), ...(m.tables ?? [])],
                exports: [...(prev.exports ?? []), ...(m.exports ?? [])],
                truncated: m.truncated || prev.truncated,
                isError: m.isError || prev.isError,
            };
            continue;
        }
        out.push(m);
    }
    return out;
};

export const AiAgent: FC = () => {
    const intl = useIntl();
    const {authInfo} = useAppSelector((state) => state.common);
    // Which business the assistant should take questions to mean. Re-sent with every
    // message too: it is switched in the sidebar, not in the chat.
    const businessId = useBusinessId();
    const userId = (authInfo as {id?: number})?.id;
    const firstName = (authInfo as Partial<AuthUserDTO>)?.firstName || null;

    const [sessionId, setSessionId] = useState<string | null>(null);
    const [messages, setMessages] = useState<ChatMsg[]>([]);
    const [sessions, setSessions] = useState<AiAgentSession[]>([]);
    const [initError, setInitError] = useState<string | null>(null);
    const [isInitializing, setIsInitializing] = useState(false);
    const [usage, setUsage] = useState<AiAgentUsage | null>(null);
    const [contactDialogOpen, setContactDialogOpen] = useState(false);
    // Below a desktop the session list is a drawer rather than a column beside the chat: fixed at
    // 280px it left about 65px of a 375px screen for the conversation itself.
    const {isCompact} = useScreenTier();
    const [sessionsOpen, setSessionsOpen] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const sessionIdRef = useRef<string | null>(null);
    // Guards the mount init against React 18 StrictMode double-invoking the effect
    // in dev — without it, restoreSession (and its reconnect) runs twice, leaving
    // an orphaned empty assistant bubble from the first, aborted reconnect stream.
    const initedForUserRef = useRef<number | null>(null);
    const {isStreaming, toolCallInProgress, isThinking, error: streamError, pendingApproval, isStopping, sendMessage, checkActiveRun, streamReconnect, submitApproval, stopRun, clearError, abortStream} = useStreamChat();

    const {data: healthData, isError: isHealthError} = aiAgentApi.useGetHealthQuery();
    const isAgentOnline = !isHealthError && healthData?.status === 'ok';

    const [initSession] = aiAgentApi.useInitSessionMutation();
    const [closeSession] = aiAgentApi.useCloseSessionMutation();
    const [lazyGetSession] = aiAgentApi.useLazyGetSessionQuery();
    const [lazyGetMessages] = aiAgentApi.useLazyGetMessagesQuery();
    const [lazyGetSessions] = aiAgentApi.useLazyGetSessionsQuery();
    const [lazyGetUsage] = aiAgentApi.useLazyGetUsageQuery();

    const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
        messagesEndRef.current?.scrollIntoView({behavior});
    }, []);

    useEffect(() => {
        // Instant scroll while streaming (a smooth animation per token is janky);
        // smooth for discrete changes (new message, stream end).
        // `pendingApproval` is included so each successive approval card (when the
        // agent requests several in a row) scrolls into view instead of appearing
        // below the fold after the previous one is resolved.
        scrollToBottom(isStreaming ? 'auto' : 'smooth');
    }, [messages, isStreaming, toolCallInProgress, pendingApproval, scrollToBottom]);

    const loadSessions = useCallback(async () => {
        if (!userId) return;
        try {
            const result = await lazyGetSessions({userId}).unwrap();
            setSessions(result.sessions);
        } catch {
            // silently fail
        }
    }, [userId, lazyGetSessions]);

    const saveSessionId = (id: string) => {
        sessionIdRef.current = id;
        setSessionId(id);
        const key = getSessionStorageKey(userId);
        if (key) sessionStorage.setItem(key, id);
    };

    const clearSessionId = () => {
        sessionIdRef.current = null;
        setSessionId(null);
        const key = getSessionStorageKey(userId);
        if (key) sessionStorage.removeItem(key);
    };

    const handleInitError = (status: number, detail?: string) => {
        if (status === 403) {
            setInitError(detail || intl.formatMessage({id: 'aiAgentBlocked'}));
        } else if (status === 422) {
            setInitError(intl.formatMessage({id: 'aiAgentSessionError'}));
        } else {
            setInitError(intl.formatMessage({id: 'aiAgentServiceUnavailable'}));
        }
    };

    // Fill the trailing assistant bubble with streamed text. Shared by fresh
    // sends and by reconnecting to an in-flight run.
    const handleToken = useCallback((fullText: string) => {
        setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            // Preserve charts/tables already attached to this bubble (via handleVisuals):
            // when tools run BEFORE any narration, the table attaches to the empty bubble
            // first and later text lands in the same segment — a plain replace would wipe it.
            updated[updated.length - 1] = {...last, role: 'assistant', content: fullText};
            return updated;
        });
        scrollToBottom('auto');  // per-token → instant, no smooth-scroll jank
    }, [scrollToBottom]);

    // Start a fresh assistant bubble mid-stream. The agent's answer spans several
    // turns and the backend persists each as its own message; splitting live keeps
    // the streaming view identical to reloaded history (one bubble per turn).
    const handleNewSegment = useCallback(() => {
        setMessages((prev) => {
            // Reuse a trailing empty placeholder (e.g. the initial send bubble, or a
            // post-approval tail with no prior narration) instead of stacking another
            // empty bubble on top of it. In the normal live flow this never triggers —
            // a segment boundary only fires after narration, so the last bubble is
            // non-empty and a fresh one is appended.
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && !last.content && !last.charts?.length && !last.tables?.length && !last.exports?.length) {
                return prev;
            }
            return [...prev, {role: 'assistant', content: ''}];
        });
    }, []);

    // Charts/tables for the turn that just finished — append to the CURRENT bubble
    // so each renders under the narration that introduced it (the next turn's text
    // starts a new bubble via handleNewSegment).
    const handleVisuals = useCallback((charts: AiAgentChartData[], tables: AiAgentTableData[], exports: AiAgentPdfExport[]) => {
        setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === 'assistant') {
                updated[updated.length - 1] = {
                    ...last,
                    charts: charts.length ? [...(last.charts ?? []), ...charts] : last.charts,
                    tables: tables.length ? [...(last.tables ?? []), ...tables] : last.tables,
                    exports: exports.length ? [...(last.exports ?? []), ...exports] : last.exports,
                };
            }
            return updated;
        });
        scrollToBottom('auto');
    }, [scrollToBottom]);

    const updateSessionTitle = useCallback((title: string) => {
        setSessions((prev) =>
            prev.map((s) => (s._id === sessionIdRef.current ? {...s, title} : s))
        );
    }, []);

    const handleDone = useCallback((result: StreamDoneResult) => {
        setMessages((prev) => {
            const updated = [...prev];
            const last = updated[updated.length - 1];
            if (last?.role === 'assistant') {
                // charts/tables are attached per-turn via handleVisuals — keep whatever
                // this bubble already has (`...last`), don't overwrite from `done`.
                updated[updated.length - 1] = {
                    ...last,
                    content: result.text || last.content,
                    truncated: result.truncated || undefined,
                    isError: result.isError,
                };
            }
            return updated;
        });
        if (result.title) {
            updateSessionTitle(result.title);
        }
        if (result.usage) {
            setUsage(result.usage);
        }
    }, [updateSessionTitle]);

    // Remove trailing empty assistant placeholder — called on transport error, or
    // when a reconnect finds no live run (the run finished in the gap between the
    // active-run check and the stream open). Keep it if it already carries
    // charts/tables (a table-only bubble has no text). Defined before restoreSession
    // so it can be referenced in the reconnect's .catch without a TDZ.
    const dropEmptyAssistant = useCallback(() => {
        setMessages((prev) => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant' && !last.content && !last.charts?.length && !last.tables?.length && !last.exports?.length) {
                return prev.slice(0, -1);
            }
            return prev;
        });
    }, []);

    const createNewSession = useCallback(async (): Promise<string | null> => {
        const token = getAuthToken();
        if (!token) {
            setInitError(intl.formatMessage({id: 'aiAgentSessionError'}));
            return null;
        }

        setIsInitializing(true);
        setInitError(null);
        clearError();

        try {
            const result = await initSession({user_token: token, active_business_id: businessId}).unwrap();
            saveSessionId(result.session_id);
            await loadSessions();
            return result.session_id;
        } catch (err: unknown) {
            const error = err as {status?: number; data?: {detail?: string}};
            handleInitError(error.status ?? 500, error.data?.detail);
            return null;
        } finally {
            setIsInitializing(false);
        }
    }, [initSession, intl, loadSessions, clearError]);

    const restoreSession = useCallback(async (storedSessionId: string) => {
        setIsInitializing(true);
        setInitError(null);
        clearError();

        try {
            await lazyGetSession(storedSessionId).unwrap();

            saveSessionId(storedSessionId);

            const msgResult = await lazyGetMessages(storedSessionId).unwrap();
            const filtered = msgResult.messages.filter(
                (m) => m.role === 'user' || (m.role === 'assistant' && (m.content || (m.charts && m.charts.length > 0) || (m.tables && m.tables.length > 0) || (m.exports && m.exports.length > 0)))
            );

            setMessages(mergeVisualOnlyBubbles(filtered));
            setTimeout(scrollToBottom, 100);

            await loadSessions();

            // The agent may still be working on this session (the user reloaded or
            // navigated away mid-answer — the backend run kept going offline). If so,
            // reconnect: append a fresh assistant bubble and replay the live stream
            // from where it is now, then keep tailing it.
            const active = await checkActiveRun(storedSessionId);
            if (active) {
                setMessages((prev) => {
                    // Defensive: drop a trailing empty assistant placeholder before
                    // adding one, so a re-entrant restore never orphans an empty bubble.
                    const last = prev[prev.length - 1];
                    const base = last?.role === 'assistant' && !last.content ? prev.slice(0, -1) : prev;
                    return [...base, {role: 'assistant', content: ''}];
                });
                // Not awaited (tail runs in the background), but DO catch: if the run
                // finished in the gap after checkActiveRun, GET /stream returns 404 →
                // onerror throws → onDone never fires. Without this the placeholder
                // bubble we just appended would be orphaned (empty, non-streaming) and
                // the rejection would go unhandled.
                streamReconnect(storedSessionId, handleToken, handleDone, handleNewSegment, handleVisuals)
                    .catch(dropEmptyAssistant);
            }
        } catch {
            // Session not found — show welcome screen
            clearSessionId();
        } finally {
            setIsInitializing(false);
        }
    }, [lazyGetSession, lazyGetMessages, loadSessions, scrollToBottom, clearError, checkActiveRun, streamReconnect, handleToken, handleDone, handleNewSegment, handleVisuals, dropEmptyAssistant]);

    const handleSelectSession = useCallback(async (selectedSessionId: string) => {
        if (selectedSessionId === sessionId) return;
        abortStream();
        await restoreSession(selectedSessionId);
    }, [sessionId, restoreSession, abortStream]);

    const handleNewChat = useCallback(async () => {
        abortStream();
        setMessages([]);
        await createNewSession();
    }, [createNewSession, abortStream]);

    const handleDeleteSession = useCallback(async (deletedSessionId: string) => {
        try {
            await closeSession(deletedSessionId).unwrap();
        } catch {
            // ignore errors
        }

        setSessions((prev) => prev.filter((s) => s._id !== deletedSessionId));

        if (deletedSessionId === sessionId) {
            abortStream();
            clearSessionId();
            setMessages([]);
        }
    }, [sessionId, closeSession, abortStream]);

    const handleSend = useCallback(async (text: string) => {
        if (!sessionId || isStreaming) return;

        setMessages((prev) => [
            ...prev,
            {role: 'user', content: text},
            {role: 'assistant', content: ''},
        ]);

        try {
            await sendMessage(sessionId, text, businessId, handleToken, handleDone, handleNewSegment, handleVisuals);
        } catch {
            dropEmptyAssistant();
        }
    }, [sessionId, isStreaming, sendMessage, handleToken, handleDone, handleNewSegment, handleVisuals, dropEmptyAssistant]);

    // Stop the agent mid-answer. The backend winds the run down and closes it with a
    // normal `done`, so the partial answer stays on screen as a finished message —
    // no need to touch the stream from here.
    const handleStop = useCallback(() => {
        if (!sessionId) return;
        void stopRun(sessionId);
    }, [sessionId, stopRun]);

    const handleWelcomeSend = useCallback(async (text: string) => {
        // Switch to chat immediately — don't wait for session creation
        setMessages([
            {role: 'user', content: text},
            {role: 'assistant', content: ''},
        ]);

        const newSessionId = await createNewSession();
        if (!newSessionId) {
            dropEmptyAssistant();
            return;
        }

        try {
            await sendMessage(newSessionId, text, businessId, handleToken, handleDone, handleNewSegment, handleVisuals);
        } catch {
            dropEmptyAssistant();
        }
    }, [createNewSession, sendMessage, handleToken, handleDone, handleNewSegment, handleVisuals, dropEmptyAssistant]);

    // Init on mount: restore stored session, or restore most recent, or show welcome screen
    useEffect(() => {
        // Logout (userId cleared) resets the guard so a later login — even as the
        // SAME user — re-runs init instead of being skipped with a stale/empty chat.
        if (!userId) {
            initedForUserRef.current = null;
            return;
        }
        // Run once per user — StrictMode (dev) mounts effects twice.
        if (initedForUserRef.current === userId) return;
        initedForUserRef.current = userId;

        const init = async () => {
            lazyGetUsage(userId).unwrap().then(setUsage).catch(() => {});

            const key = getSessionStorageKey(userId);
            const stored = key ? sessionStorage.getItem(key) : null;
            if (stored) {
                await restoreSession(stored);
                return;
            }

            // No stored session — load sidebar list but stay on welcome screen
            try {
                const result = await lazyGetSessions({userId}).unwrap();
                setSessions(result.sessions);
            } catch {
                // fail silently
            }
        };

        void init();
    }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

    const lastMsg = messages[messages.length - 1];
    const isLastMessageStreaming = isStreaming && lastMsg?.role === 'assistant';
    const showWelcome = !sessionId && !isInitializing && !initError && messages.length === 0;
    const showChat = (sessionId !== null || messages.length > 0) && !initError;

    return (
        <Box sx={aiAgentSx.wrapper}>
            <Box sx={aiAgentSx.root}>
                {isCompact ? (
                    <Drawer
                        anchor="left"
                        open={sessionsOpen}
                        onClose={() => setSessionsOpen(false)}
                        ModalProps={{keepMounted: true}}
                    >
                        {/* Picking a conversation or starting one closes the drawer: on a phone the
                            thing chosen is behind it. */}
                        <SessionSidebar
                            sessions={sessions}
                            activeSessionId={sessionId}
                            onSelectSession={(id) => {
                                handleSelectSession(id);
                                setSessionsOpen(false);
                            }}
                            onNewChat={() => {
                                handleNewChat();
                                setSessionsOpen(false);
                            }}
                            onDeleteSession={handleDeleteSession}
                        />
                    </Drawer>
                ) : (
                    <SessionSidebar
                        sessions={sessions}
                        activeSessionId={sessionId}
                        onSelectSession={handleSelectSession}
                        onNewChat={handleNewChat}
                        onDeleteSession={handleDeleteSession}
                    />
                )}

                <Box sx={aiAgentSx.chatArea}>
                    <Box sx={aiAgentSx.header}>
                        {isCompact && (
                            <IconButton
                                onClick={() => setSessionsOpen(true)}
                                sx={aiAgentSx.sessionsButton}
                                aria-label={intl.formatMessage({id: 'aiAssistantSidebar'})}
                            >
                                <HistoryOutlinedIcon fontSize="small"/>
                            </IconButton>
                        )}
                        <Box sx={aiAgentSx.headerAvatarWrapper}>
                            <Box sx={aiAgentSx.headerAvatarPulse} />
                            <Box sx={aiAgentSx.headerAvatar}>
                                <SmartToyOutlinedIcon fontSize="inherit" />
                            </Box>
                        </Box>
                        <Box sx={aiAgentSx.headerInfo}>
                            <Typography variant="h5" fontWeight={600}>
                                {intl.formatMessage({id: 'aiAssistantSidebar'})}
                            </Typography>
                            <Box sx={aiAgentSx.headerOnline}>
                                <Box sx={isAgentOnline ? aiAgentSx.onlineDot : aiAgentSx.offlineDot} />
                                <Typography variant="caption" color="text.secondary">
                                    {intl.formatMessage({id: isAgentOnline ? 'aiAgentOnline' : 'aiAgentOffline'})}
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={aiAgentSx.headerActions}>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<SupportAgentOutlinedIcon/>}
                                onClick={() => setContactDialogOpen(true)}
                                sx={aiAgentSx.talkToHumanButton}
                            >
                                {intl.formatMessage({id: 'aiAgentTalkToHuman'})}
                            </Button>
                        </Box>
                    </Box>

                    {/* Welcome screen */}
                    {showWelcome && (
                        <Box sx={aiAgentSx.welcome}>
                            <Box sx={aiAgentSx.welcomeIconWrapper}>
                                <Box sx={aiAgentSx.welcomeIconPulse} />
                                <Box sx={aiAgentSx.welcomeIcon}>
                                    <SmartToyOutlinedIcon fontSize="inherit" />
                                </Box>
                            </Box>
                            <Box sx={{textAlign: 'center', zIndex: 1}}>
                                <Typography variant="h3" sx={aiAgentSx.welcomeGreeting} gutterBottom>
                                    {firstName
                                        ? intl.formatMessage({id: 'aiAgentWelcomeGreeting'}, {name: firstName})
                                        : intl.formatMessage({id: 'aiAgentWelcomeGreetingNoName'})
                                    }
                                </Typography>
                                <Typography variant="body1" sx={{maxWidth: 480, mx: 'auto', color: '#64748B'}}>
                                    {intl.formatMessage({id: 'aiAgentWelcomeSubtitle'})}
                                </Typography>
                            </Box>
                            <Box sx={aiAgentSx.welcomeInputWrapper}>
                                <ChatInput
                                    onSend={handleWelcomeSend}
                                    disabled={isInitializing}
                                    placeholder={intl.formatMessage({id: 'aiAgentWelcomePlaceholder'})}
                                    elevated
                                />
                            </Box>
                        </Box>
                    )}

                    {/* Loading — only when no messages yet (e.g. restoring session) */}
                    {isInitializing && messages.length === 0 && (
                        <Box sx={aiAgentSx.loading}>
                            <CircularProgress size={32} />
                            <Typography variant="body2" color="text.secondary">
                                {intl.formatMessage({id: 'aiAgentThinking'})}
                            </Typography>
                        </Box>
                    )}

                    {/* Init error */}
                    {initError && !isInitializing && (
                        <Box sx={aiAgentSx.error}>
                            <Typography color="error">{initError}</Typography>
                        </Box>
                    )}

                    {/* Chat messages */}
                    {showChat && (
                        <Box sx={aiAgentSx.messages}>
                            {messages.map((msg, idx) => {
                                const isStreamingMsg = idx === messages.length - 1 && isLastMessageStreaming;
                                // Hide the streaming placeholder only while it's truly empty — a
                                // table-only bubble (charts/tables/exports, no text) must still show live.
                                if (isStreamingMsg && !msg.content && !msg.charts?.length && !msg.tables?.length && !msg.exports?.length) return null;
                                return (
                                    <ChatMessage
                                        key={idx}
                                        role={msg.role}
                                        content={msg.content}
                                        isStreaming={isStreamingMsg}
                                        charts={msg.charts}
                                        tables={msg.tables}
                                        exports={msg.exports}
                                        allMessages={messages}
                                        isError={msg.isError}
                                    />
                                );
                            })}
                            {isStreaming && toolCallInProgress && (
                                <ToolCallIndicator toolName={toolCallInProgress} />
                            )}
                            {isStreaming && !toolCallInProgress && !pendingApproval
                                && (isThinking || (!lastMsg?.content && !lastMsg?.charts?.length && !lastMsg?.tables?.length && !lastMsg?.exports?.length)) && (
                                <ToolCallIndicator />
                            )}
                            {pendingApproval && sessionId && (
                                <ApprovalCard
                                    summary={pendingApproval.summary}
                                    actions={pendingApproval.actions}
                                    title={pendingApproval.title}
                                    onApprove={() => submitApproval(sessionId, pendingApproval.runId, pendingApproval.toolCallId, true)}
                                    onDeny={() => submitApproval(sessionId, pendingApproval.runId, pendingApproval.toolCallId, false)}
                                />
                            )}
                            {streamError && (
                                <Box sx={{mb: 2}}>
                                    <Typography variant="body2" color="error">
                                        {intl.formatMessage({id: 'aiAgentError'})}
                                    </Typography>
                                </Box>
                            )}
                            <div ref={messagesEndRef} />
                        </Box>
                    )}

                    {/* Bottom input — when session exists or messages already showing */}
                    {showChat && (
                        <>
                            {usage && <UsageBar usage={usage} />}
                            <ChatInput
                                onSend={handleSend}
                                disabled={isStreaming || isInitializing}
                                isStreaming={isStreaming}
                                isStopping={isStopping}
                                onStop={handleStop}
                            />
                        </>
                    )}
                </Box>
            </Box>

            <ContactSupportDialog
                open={contactDialogOpen}
                onClose={() => setContactDialogOpen(false)}
                sessionId={sessionId}
            />
        </Box>
    );
};
